import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const BUBBLE_ME = "ml-auto bg-[#1a1a1a] text-white bo-inv";
const BUBBLE_THEM = "mr-auto border border-[#d0d0d0] bg-white text-[#1a1a1a]";

const clock = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
};

/**
 * The live chat line (ADD-ON 30) — docked in the corner of every back-office screen so an owner
 * never has to leave the dashboard to ask a question. Polls while it's open, and quietly every
 * half minute while it's shut so the badge stays honest.
 */
export default function ChatDock() {
    const [open, setOpen] = useState(false);
    const [d, setD] = useState(null);
    const [unread, setUnread] = useState(0);
    const [line, setLine] = useState(null);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [off, setOff] = useState(false);
    const endRef = useRef(null);

    const ping = useCallback(() => {
        api.get("/support/unread")
            .then(({ data }) => { setUnread(data.unread || 0); setLine(data.line); })
            .catch(() => setOff(true));
    }, []);

    const load = useCallback(() => api.get("/support")
        .then(({ data }) => { setD(data); setLine(data.line); setUnread(0); api.post("/support/read").catch(() => { }); })
        .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setOff(true); }), []);

    useEffect(() => { ping(); const t = setInterval(ping, 30000); return () => clearInterval(t); }, [ping]);
    useEffect(() => {
        if (!open) return;
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, [open, load]);
    useEffect(() => { if (open) endRef.current?.scrollIntoView({ block: "end" }); }, [open, d]);

    const send = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if (!body) return;
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/support", { body });
            setD((prev) => ({ ...(prev || {}), messages: data.messages, thread: data.thread, line: data.line }));
            setText("");
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Couldn't send that"); }
        finally { setBusy(false); }
    };

    if (off && !open) return null;

    return (
        <>
            {!open && (
                <button onClick={() => setOpen(true)} data-testid="chat-dock-open"
                    className="fixed bottom-4 right-4 z-[70] inline-flex items-center gap-2 border border-[#1a56db] bg-[#1a56db] px-3 py-2 text-[13px] font-bold text-white bo-inv hover:bg-[#1544b0]">
                    <MessageSquare size={14} /> Live chat
                    {unread > 0 && (
                        <span className="grid h-4 min-w-4 place-items-center bg-white px-1 text-[11px] font-bold text-[#1a56db]"
                            data-testid="chat-dock-badge">{unread}</span>
                    )}
                </button>
            )}

            {open && (
                <div className="fixed bottom-4 right-4 z-[70] flex h-[26rem] w-[min(22rem,calc(100vw-2rem))] flex-col border border-[#d0d0d0] bg-white shadow-2xl"
                    data-testid="chat-dock">
                    <header className="flex items-center gap-2 border-b border-[#d0d0d0] bg-[#1a1a1a] px-2.5 py-2 bo-inv">
                        <MessageSquare size={14} className="text-white" />
                        <div className="min-w-0">
                            <p className="text-[13px] font-bold leading-tight text-white">Live chat · 13thav support</p>
                            <p className="text-[11px] leading-tight text-white/70" data-testid="chat-dock-hours">
                                {line?.open ? "Open now" : "Away"} · {line?.hours_label || "Mon–Fri 9–6 ET"}
                            </p>
                        </div>
                        <button onClick={() => setOpen(false)} data-testid="chat-dock-close" aria-label="Close chat"
                            className="ml-auto grid h-6 w-6 place-items-center text-white hover:bg-white/15">
                            <X size={14} />
                        </button>
                    </header>

                    <div className="flex-1 space-y-2 overflow-y-auto bg-[#f5f5f5] p-2.5" data-testid="chat-dock-messages">
                        {!d && <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-[#4a4a4a]" /></div>}
                        {d && (d.messages || []).length === 0 && (
                            <p className="px-1 text-[13px] leading-snug text-[#4a4a4a]" data-testid="chat-dock-empty">
                                Ask us anything — pricing, consignment, a stuck order, your domain.
                                {line?.open ? " Somebody's on the line right now." : ` ${line?.note || ""}`}
                            </p>
                        )}
                        {(d?.messages || []).map((m) => (
                            <div key={m.id} data-testid={`chat-msg-${m.from}`}
                                className={`max-w-[85%] px-2.5 py-1.5 text-[13px] leading-snug ${m.from === "owner" ? BUBBLE_ME : BUBBLE_THEM}`}>
                                {m.body}
                                <span className={`mt-0.5 block text-[11px] ${m.from === "owner" ? "text-white/60" : "text-[#4a4a4a]"}`}>
                                    {m.from === "owner" ? "you" : "13thav support"} · {clock(m.created_at)}
                                </span>
                            </div>
                        ))}
                        <div ref={endRef} />
                    </div>

                    {!line?.open && (
                        <p className="border-t border-[#d0d0d0] bg-[#fffaeb] px-2.5 py-1.5 text-[11px] leading-snug text-[#b54708]"
                            data-testid="chat-dock-away">{line?.note}</p>
                    )}
                    {err && (
                        <p className="border-t border-[#d0d0d0] bg-[#fef3f2] px-2.5 py-1.5 text-[11px] font-semibold text-[#b42318]"
                            data-testid="chat-dock-error">{err}</p>
                    )}

                    <form onSubmit={send} className="flex items-center gap-1.5 border-t border-[#d0d0d0] p-1.5">
                        <input value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-dock-input"
                            placeholder="Type a question…" maxLength={2000}
                            className="w-full border border-[#d0d0d0] bg-white px-2 py-1.5 text-sm text-[#1a1a1a] outline-none placeholder:text-[#767676] focus:border-[#1a1a1a]" />
                        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-dock-send"
                            className="inline-flex shrink-0 items-center gap-1 border border-[#1a56db] bg-[#1a56db] px-2.5 py-1.5 text-[13px] font-bold text-white bo-inv hover:bg-[#1544b0] disabled:opacity-50">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
